import { useFlag } from '@calendly/feature-check-react';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Link } from '@calendly/ui/components/link';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { useNotetakerSettingsContext } from 'features/meeting_intelligence/settings/notetaker_settings_provider';
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context';
import { IntegrationsBody } from './integrations_body';
import { RecordingDefaultsBody } from './recording_defaults_body';
import { RecordingRulesSwitch } from './recording_rules_switch';
import { SectionContentWrapper, SectionTitleWrapper, SectionWrapper, TitleWrapper, SectionRightContentRow, ConnectedWrapper, MobileSectionTitleWrapper, MobileTitleWrapper, MobileSectionWrapper } from './styles';

// Calendar type display name mapping
// To add a new calendar type, simply add an entry here
const CALENDAR_TYPE_DISPLAY_NAMES = {
  outlook: 'Outlook Calendar',
  google: 'Google Calendar'
  // Add more calendar types here as needed
  // Example: icloud: 'iCloud Calendar',
  //          exchange: 'Exchange Calendar',
};
const getCalendarTypeDisplayName = kind => {
  if (!kind) return 'UNKNOWN CALENDAR TYPE';
  return CALENDAR_TYPE_DISPLAY_NAMES[kind.toLowerCase()] || 'UNKNOWN CALENDAR TYPE';
};
const previewLinkCss = "p1jr713f";
const PreviewLink = ({
  children
}) => <Link className={previewLinkCss} href="https://calendly.com/help/notetaker-overview#advance-notice" target="_blank" rel="noopener noreferrer" underline={false}>
    {children}
  </Link>;
const settingsModalSectionWrapperCss = "s1nel4hf";
const settingsModalSectionTitleWrapperCss = "sc7qtbx";
const preMeetingRecordingNoticeDescription = () => HtmlParser(I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.pre_meeting_recording_notice.description'), {
  PreviewLink
});
const getDictionary = kind => {
  const calendarType = getCalendarTypeDisplayName(kind);
  return {
    calendlyMeetings: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.calendly_meetings.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.calendly_meetings.description')
    },
    connectedNonCalendlyMeetings: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.connected_meetings.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.connected_meetings.description', {
        calendar_type: calendarType
      })
    },
    externalInviteeMeetings: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.external_meetings.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.external_meetings.description')
    },
    autoShareRecapEmails: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.description')
    },
    preMeetingRecordingNotice: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.pre_meeting_recording_notice.title')
    },
    integrations: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.description')
    }
  };
};

// Main settings component which contains all settings sections.
// autoShareRecapEmails is sourced from NotetakerSettingsProvider (useNotetakerSettingsContext).
export const NotetakerSettings = ({
  kind
} = {}) => {
  const isDesktop = useDeviceType('isDesktopUp');
  const inSettingsModal = useIsSettingsModalLayout();
  const automatedRecapSharingEnabled = useFlag('automated_recap_sharing');
  const notetakerNoticeEmailEnabled = useFlag('notetaker_notice_email');
  const integrationsCardEnabled = useFlag('integrations_card');
  const {
    autoShareRecapEmails,
    setAutoShareRecapEmails,
    preMeetingRecordingNotice,
    setPreMeetingRecordingNotice
  } = useNotetakerSettingsContext();
  const dictionary = getDictionary(kind);
  const modalSectionClassName = inSettingsModal ? settingsModalSectionWrapperCss : undefined;
  const modalSectionTitleClassName = inSettingsModal ? settingsModalSectionTitleWrapperCss : undefined;
  return isDesktop ? <SectionContentWrapper data-testid="recording-rules-desktop">
      <SectionWrapper className={modalSectionClassName}>
        <SectionTitleWrapper className={modalSectionTitleClassName}>
          <TitleText size="small">
            {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults_section_title')}
          </TitleText>
        </SectionTitleWrapper>
        <RecordingDefaultsBody />
      </SectionWrapper>
      {automatedRecapSharingEnabled && <SectionWrapper className={modalSectionClassName}>
          <SectionTitleWrapper className={modalSectionTitleClassName}>
            <TitleText size="small">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recap_sharing_section_title')}
            </TitleText>
          </SectionTitleWrapper>
          <SectionRightContentRow>
            <TitleWrapper>
              <BodyText size="medium" weight="bold">
                {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.title')}
              </BodyText>
              <BodyText size="medium" color="muted">
                {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.description')}
              </BodyText>
            </TitleWrapper>
            <RecordingRulesSwitch value={autoShareRecapEmails} onChange={setAutoShareRecapEmails} />
          </SectionRightContentRow>
        </SectionWrapper>}
      {notetakerNoticeEmailEnabled && <SectionWrapper className={modalSectionClassName}>
          <SectionTitleWrapper className={modalSectionTitleClassName}>
            <TitleText size="small">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_notice_section_title')}
            </TitleText>
          </SectionTitleWrapper>
          <SectionRightContentRow>
            <TitleWrapper>
              <BodyText size="medium" weight="bold">
                {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.pre_meeting_recording_notice.title')}
              </BodyText>
              <BodyText size="medium" color="muted">
                {preMeetingRecordingNoticeDescription()}
              </BodyText>
            </TitleWrapper>
            <RecordingRulesSwitch value={preMeetingRecordingNotice} onChange={setPreMeetingRecordingNotice} />
          </SectionRightContentRow>
        </SectionWrapper>}
      {integrationsCardEnabled && <SectionWrapper className={modalSectionClassName}>
          <SectionTitleWrapper className={modalSectionTitleClassName}>
            <TitleText size="small">{dictionary.integrations.title}</TitleText>
            <BodyText color="muted" size="medium">
              {dictionary.integrations.description}
            </BodyText>
          </SectionTitleWrapper>
          <ConnectedWrapper>
            <IntegrationsBody />
          </ConnectedWrapper>
        </SectionWrapper>}
    </SectionContentWrapper> : <SectionContentWrapper data-testid="recording-rules-mobile">
      <MobileSectionWrapper>
        <MobileSectionTitleWrapper>
          <TitleText size="small">
            {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults_section_title')}
          </TitleText>
        </MobileSectionTitleWrapper>
        <RecordingDefaultsBody />
      </MobileSectionWrapper>
      {automatedRecapSharingEnabled && <MobileSectionWrapper>
          <MobileSectionTitleWrapper>
            <TitleText size="small">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recap_sharing_section_title')}
            </TitleText>
          </MobileSectionTitleWrapper>
          <MobileTitleWrapper>
            <TitleWrapper>
              <BodyText size="medium" weight="bold">{dictionary.autoShareRecapEmails.title}</BodyText>
              <BodyText size="medium" color="muted">{dictionary.autoShareRecapEmails.description}</BodyText>
            </TitleWrapper>
            <RecordingRulesSwitch value={autoShareRecapEmails} onChange={setAutoShareRecapEmails} />
          </MobileTitleWrapper>
        </MobileSectionWrapper>}
      {notetakerNoticeEmailEnabled && <MobileSectionWrapper>
          <MobileSectionTitleWrapper>
            <TitleText size="small">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_notice_section_title')}
            </TitleText>
          </MobileSectionTitleWrapper>
          <MobileTitleWrapper>
            <TitleWrapper>
              <BodyText size="medium" weight="bold">{dictionary.preMeetingRecordingNotice.title}</BodyText>
              <BodyText size="medium" color="muted">{preMeetingRecordingNoticeDescription()}</BodyText>
            </TitleWrapper>
            <RecordingRulesSwitch value={preMeetingRecordingNotice} onChange={setPreMeetingRecordingNotice} />
          </MobileTitleWrapper>
        </MobileSectionWrapper>}
      {integrationsCardEnabled && <MobileSectionWrapper>
          <MobileSectionTitleWrapper>
            <TitleText size="small">
              {dictionary.integrations.title}
            </TitleText>
            <BodyText size="medium" color="muted">{dictionary.integrations.description}</BodyText>
          </MobileSectionTitleWrapper>
          <IntegrationsBody />
        </MobileSectionWrapper>}
    </SectionContentWrapper>;
};

require("./notetaker_settings.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./notetaker_settings.js");